import type { ReactElement, ReactNode } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { Size } from "../helpers/helpers.js";
import { SimpleTooltip } from "../tooltip/simpleTooltip.js";
import { TooltipTrigger } from "../tooltip/tooltipTrigger.js";
import { toggleButtonGroupStyle } from "./style.js";

export interface SegmentedControlOption<T extends string> {
  readonly id: T;
  /** The segment's text, or its accessible name and tooltip when `isIconOnly`. */
  readonly label: string;
  /** Rendered before the label (or alone when `isIconOnly`); pass a sized icon element. */
  readonly icon?: ReactNode;
  readonly isDisabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  /** The options rendered as segments, in display order. */
  readonly options: readonly SegmentedControlOption<T>[];
  /** The currently selected option id. */
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Accessible label for the group; required so screen readers can name it. */
  readonly "aria-label": string;
  readonly size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /** Render square icon-only segments, with each label as a tooltip. */
  readonly isIconOnly?: boolean;
  readonly className?: string;
}

/**
 * A single-select segmented control: a compact pill of mutually exclusive
 * options, e.g. an Active/All list filter or a display-mode switch. The
 * single-select sibling of {@link ToggleChipGroup}, sharing react-aria's
 * ToggleButtonGroup semantics and the {@link toggleButtonGroupStyle} look.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
  size = Size.MEDIUM,
  isIconOnly = false,
  className,
}: SegmentedControlProps<T>): ReactElement {
  const style = toggleButtonGroupStyle({ size, hasLabel: !isIconOnly });
  return (
    <ToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      aria-label={ariaLabel}
      onSelectionChange={(keys) => {
        const selected = options.find((option) => keys.has(option.id));
        if (selected) {
          onChange(selected.id);
        }
      }}
      className={style.group({ className })}
    >
      {options.map((option) =>
        isIconOnly ? (
          <TooltipTrigger key={option.id}>
            <ToggleButton
              id={option.id}
              aria-label={option.label}
              isDisabled={option.isDisabled}
              className={style.button()}
            >
              {option.icon}
            </ToggleButton>
            <SimpleTooltip>{option.label}</SimpleTooltip>
          </TooltipTrigger>
        ) : (
          <ToggleButton
            key={option.id}
            id={option.id}
            isDisabled={option.isDisabled}
            className={style.button()}
          >
            {option.icon}
            {option.label}
          </ToggleButton>
        ),
      )}
    </ToggleButtonGroup>
  );
}
